import { Conflict, LineNumber, Quantity, Weights } from 'constants/products';
import { Eligibility, Metadata } from 'api/definitions/basket-builder-experience/types';
import type { TransformedProducts } from './transform-products-and-recommendations-into-legacy-format';
import mapProductsIntoLegacyFormat from './transform-product-into-legacy-format';
import { RecommendedSelection } from './types';

export type SuggestionLike = {
  conflicts?: Conflict[];
  id: string;
  lineNumber: LineNumber;
  suggestedQuantity?: Quantity;
  weights: Weights;
};

export type BasketBuilderExperienceSuggestionsResponseLike = {
  eligibility: Eligibility;
  metadata?: Pick<Metadata, 'totalResults'>;
  suggestions: SuggestionLike[];
};

export type TransformedBasketBuilderExperienceSuggestionsResponse<
  TResponse extends BasketBuilderExperienceSuggestionsResponseLike,
> = TransformedProducts<TResponse> & {
  recommendedSelection: RecommendedSelection[];
};

const createRecommendedSelection = ({
  metadata,
  suggestions,
}: {
  metadata?: Pick<Metadata, 'totalResults'>;
  suggestions: SuggestionLike[];
}) => {
  const topBuyableSuggestions = suggestions
    .slice(0, metadata?.totalResults)
    .filter(suggestion => !suggestion.conflicts?.length);

  return topBuyableSuggestions.reduce<RecommendedSelection[]>((selection, topSuggestion) => {
    const { suggestedQuantity, weights, lineNumber, id } = topSuggestion;
    const quantity = suggestedQuantity ?? weights?.defaultQuantity;

    if (!quantity) {
      return selection;
    }

    selection.push({
      lineNumber,
      id,
      quantity: {
        amount: quantity.amount,
        uomId: quantity.uom,
      },
    });

    return selection;
  }, []);
};

const transformBasketBuilderSuggestionsIntoLegacyFormat = <
  TResponse extends BasketBuilderExperienceSuggestionsResponseLike,
>(
  response: TResponse,
): TransformedBasketBuilderExperienceSuggestionsResponse<TResponse> => {
  return {
    ...(mapProductsIntoLegacyFormat(response) as TransformedProducts<TResponse>),
    recommendedSelection: createRecommendedSelection(response),
  };
};

export default transformBasketBuilderSuggestionsIntoLegacyFormat;
